import {useCallback, useEffect, useMemo, useState} from 'react';

import {useCurrentValueInputTypingStore} from 'presentation/components/Filter/store/useCurrentValueInputTypingStore';
import {useIsAutocompleteFocusedStore} from 'presentation/components/Filter/store/useIsAutocompleteFocusedStore';
import {useGetHealthMultiAvailableTimesQuery} from 'presentation/modules/booking/shared/queries/useGetHealthMultiAvailableTimesQuery';
import {useMediaQueries, useQueryParams} from 'presentation/pages';
import {isUnprocessableError} from 'presentation/utils/isUnprocessableError';
import {textHandle} from 'presentation/utils/textHandle';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {FeaturesEnum} from 'presentation/constants';
import {MultiAvailableTimes} from '../../../../hooks/useHealthProfessionalSelection/types';
import {UseHealthNearestDateProfessionalListProps} from './types';

export function useHealthNearestDateProfessionalList({
	availableDates,
	filteredProvidersNameOptions,
}: UseHealthNearestDateProfessionalListProps) {
	const {isMobile} = useMediaQueries();
	const [datesState, setDatesState] = useState<string[]>(availableDates);
	const [availableTimesState, setAvailableTimesState] = useState<MultiAvailableTimes[]>([]);
	const [isFetchingMoreState, setIsFetchingMoreState] = useState(false);
	const currentValueInputTypingState = useCurrentValueInputTypingStore(
		state => state.currentValueInputTyping,
	);
	const setCurrentValueInputTypingState = useCurrentValueInputTypingStore(
		state => state.setCurrentValueInputTyping,
	);
	const isAutocompleteFocusedState = useIsAutocompleteFocusedStore(
		state => state.isAutocompleteFocused,
	);

	const {
		SEARCH_PARAMS: {shift, query, autocompleteQuery},
	} = useQueryParams();

	const normalizedQuery = textHandle(query).sanitizeText();
	const queryToFilter = normalizedQuery === 'clear' ? '' : normalizedQuery;

	const {
		data: healthMultiAvailableTimes,
		isFetching: isFetchingHealthMultiAvailableTimes,
		error: errorHealthMultiAvailableTimes,
		refetch: healthMultiAvailableTimesRefetch,
	} = useGetHealthMultiAvailableTimesQuery({
		isTelemedicine: false,
		dates: datesState,
		enabled: true,
	});

	const notFoundHealthMultiAvailableTimesError = isUnprocessableError(
		errorHealthMultiAvailableTimes,
	);

	const healthMultiAvailableTimesFiltered = useMemo(() => {
		const filteredErrorQueries = availableTimesState.filter(({times}) => times.length > 0);

		if (!autocompleteQuery) {
			return filteredErrorQueries.map(availableTimes => ({
				...availableTimes,
				times: availableTimes.times.filter(({professionalName}) =>
					textHandle(professionalName).sanitizeText().includes(queryToFilter),
				),
			}));
		}

		return filteredErrorQueries;
	}, [availableTimesState, autocompleteQuery, queryToFilter]);

	const hasNoAvailableTimes = healthMultiAvailableTimesFiltered.every(
		list => list.times.length === 0,
	);
	const hasFilteredOptions = filteredProvidersNameOptions.length > 0;
	const hasAccumulatedData = availableTimesState.length > 0;

	const shouldShowMobileOptions = isAutocompleteFocusedState && hasFilteredOptions && isMobile;

	const isError = errorHealthMultiAvailableTimes && !notFoundHealthMultiAvailableTimesError;

	const isLoading =
		(isFetchingHealthMultiAvailableTimes || isFetchingMoreState) && !hasAccumulatedData;

	const showNotFoundState =
		(!hasAccumulatedData && !healthMultiAvailableTimes) ||
		(!hasAccumulatedData && healthMultiAvailableTimes?.length === 0 && !isFetchingMoreState) ||
		notFoundHealthMultiAvailableTimesError ||
		(hasNoAvailableTimes && !isFetchingMoreState && !hasAccumulatedData) ||
		(!hasFilteredOptions && currentValueInputTypingState);

	const shouldShowAvailableTimesList =
		healthMultiAvailableTimesFiltered.some(list => list.times.length > 0) &&
		!(isAutocompleteFocusedState && isMobile) &&
		!showNotFoundState;

	const isSentinelOnVisibleActive =
		healthMultiAvailableTimesFiltered.length > 0 &&
		hasFilteredOptions &&
		healthMultiAvailableTimes &&
		!isFetchingMoreState;

	const shouldNotUseHighlight = Boolean(autocompleteQuery || hasFilteredOptions);

	const isSentinelLoading =
		availableTimesState?.length > 0 && isFetchingHealthMultiAvailableTimes;

	const {isEnabled: isPreviousProviderEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_BOOKING_PREVIOUS_PROVIDER,
	);

	const shouldSkipPreviousProvider = !isPreviousProviderEnabled || Boolean(autocompleteQuery);

	const previousProviderData = useMemo(() => {
		if (shouldSkipPreviousProvider) return null;

		const dates = healthMultiAvailableTimesFiltered
			?.flatMap(({date, times}) => {
				const provider = times.find(t => t.isPreviousProvider);
				if (!provider) return [];
				return [{date, availableTimes: provider.availableTimes}];
			})
			.filter(item => item.availableTimes.length > 0);

		if (!dates?.length) return null;

		const provider = healthMultiAvailableTimesFiltered
			?.flatMap(({times}) => times)
			.find(t => t.isPreviousProvider);

		if (!provider) return null;

		return {
			professionalName: provider.professionalName,
			professionalCode: provider.professionalCode,
			recommendations: provider.recommendations,
			isFavorite: provider.isFavorite,
			dates,
		};
	}, [healthMultiAvailableTimesFiltered, shouldSkipPreviousProvider]);

	const timesWithoutPreviousProvider = useMemo(() => {
		if (shouldSkipPreviousProvider) return healthMultiAvailableTimesFiltered;
		return healthMultiAvailableTimesFiltered?.map(({date, times}) => ({
			date,
			times: times.filter(t => !t.isPreviousProvider),
		}));
	}, [healthMultiAvailableTimesFiltered, shouldSkipPreviousProvider]);

	const showPreviousProvider = !shouldSkipPreviousProvider && previousProviderData !== null;

	const loadMoreDates = useCallback(() => {
		if (isFetchingMoreState) return;
		setIsFetchingMoreState(true);
		setDatesState(prevDates => {
			if (prevDates.length === 0) return availableDates.slice(0, 3);

			const lastDate = prevDates[prevDates.length - 1];
			const nextStartIndex = availableDates.findIndex(date => date === lastDate) + 1;

			const nextDates = availableDates.slice(nextStartIndex, nextStartIndex + 3);

			if (nextDates.length === 0) return prevDates;

			return nextDates;
		});
	}, [availableDates, isFetchingMoreState]);

	useEffect(() => {
		setDatesState(availableDates.slice(0, 3));
		setAvailableTimesState([]);
		setIsFetchingMoreState(true);
	}, [autocompleteQuery, shift]);

	useEffect(() => {
		if (healthMultiAvailableTimes) {
			setAvailableTimesState(prev => {
				const uniqueDates = new Set(prev?.map(item => item?.date));
				const newTimes = healthMultiAvailableTimes?.filter(
					({date}) => !uniqueDates.has(date),
				);

				return [...prev, ...newTimes];
			});
			setIsFetchingMoreState(false);
		}
	}, [healthMultiAvailableTimes]);

	return {
		isLoading,
		isError,
		healthMultiAvailableTimesRefetch,
		showNotFoundState,
		shouldShowMobileOptions,
		shouldShowAvailableTimesList,
		isAutocompleteFocusedState,
		autocompleteQuery,
		isSentinelOnVisibleActive,
		loadMoreDates,
		isSentinelLoading,
		shouldNotUseHighlight,
		query,
		setCurrentValueInputTypingState,
		previousProviderData,
		timesWithoutPreviousProvider,
		showPreviousProvider,
	};
}
